// ✅ src/pages/VehicleV2/BatteryCardMaintenanceReport.jsx
// UPDATED FULL FILE
//
// ✅ Updated pattern similar to BranchwiseVehicleReportListing / OwnVehicleReport
// ✅ common localStorage / sessionStorage header pattern
// ✅ useMemo + useCallback pattern
// ✅ pageLoading + dropdownLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ safer search / reset / refresh / pagination / print flow
// ✅ uses inventory headers with backend inventory-pool version

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaSyncAlt,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "http://nissi-india.com/api/batteryCardMaintenanceReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const fmtMoney = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return v ?? "";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const fmtDate = (v) => (v ? String(v) : "");

const normalizeOptionList = (arr, fallbackLabel = "Select", fallbackValue = "Select") => {
  const list = toList(arr).map((x) => {
    if (typeof x === "string" || typeof x === "number") {
      return { label: String(x), value: String(x) };
    }

    return {
      label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
      value: String(x.value ?? x.id ?? x.key ?? fallbackValue),
      ...x,
    };
  });

  return list.length ? list : [{ label: fallbackLabel, value: fallbackValue }];
};

const createDefaultFilters = (branchType, branchId) => ({
  fromDate: "",
  toDate: "",
  selectedBranchId: branchType === "HO" ? "Select" : String(branchId),

  vehicleCode: "",
  vehicleRegNo: "",
  vehicleName: "",

  batteryCode: "Select",
  batteryType: "Select",
  batterySerialNo: "Select",

  batteryStatus: "Select",
  vehicleMainTypeIndex: "Select",
  vehicleStatus: "Select",

  store: "Select",
});

export default function BatteryCardMaintenanceReport() {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     meta/dropdown states
  =========================== */
  const [meta, setMeta] = useState({
    branches: [{ value: "Select", label: "Select" }],
    batteryCodes: [{ value: "Select", label: "Select" }],
    batteryTypes: [{ value: "Select", label: "Select" }],
    serialNos: [{ value: "Select", label: "Select" }],
    vehicleMainTypes: [{ value: "Select", label: "Select" }],
    batteryStatuses: [{ value: "Select", label: "Select" }],
    vehicleStatuses: [{ value: "Select", label: "Select" }],
  });

  /* ===========================
     filters + list states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  /* ===========================
     computed
  =========================== */
  const totalRows = rows.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRows / pageSize)),
    [totalRows, pageSize]
  );

  const indexOfLastItem = page * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;

  const currentItems = useMemo(
    () => rows.slice(indexOfFirstItem, indexOfLastItem),
    [rows, indexOfFirstItem, indexOfLastItem]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const tdClass = useMemo(
    () =>
      `px-4 py-4 text-sm text-center ${
        isDark ? "text-gray-300" : "text-gray-900"
      }`,
    [isDark]
  );

  const thClass = "px-4 py-4 text-center font-semibold";

  /* ===========================
     helpers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  /* ===========================
     meta load
  =========================== */
  const loadMeta = useCallback(
    async (selectedBranchId) => {
      try {
        setDropdownLoading(true);

        const { data } = await axios.get(`${API_BASE}/meta`, {
          params: {
            branchType: session.branchType,
            branchId: session.branchId,
            loginUserId: session.loginUserId,
            selectedBranchId:
              selectedBranchId ??
              filters.selectedBranchId ??
              defaultFilters.selectedBranchId,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load meta");
        }

        setMeta({
          branches: normalizeOptionList(data.branches, "Select", "Select"),
          batteryCodes: normalizeOptionList(data.batteryCodes, "Select", "Select"),
          batteryTypes: normalizeOptionList(data.batteryTypes, "Select", "Select"),
          serialNos: normalizeOptionList(data.serialNos, "Select", "Select"),
          vehicleMainTypes: normalizeOptionList(data.vehicleMainTypes, "Select", "Select"),
          batteryStatuses: normalizeOptionList(data.batteryStatuses, "Select", "Select"),
          vehicleStatuses: normalizeOptionList(data.vehicleStatuses, "Select", "Select"),
        });
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");

        setMeta({
          branches: [{ value: "Select", label: "Select" }],
          batteryCodes: [{ value: "Select", label: "Select" }],
          batteryTypes: [{ value: "Select", label: "Select" }],
          serialNos: [{ value: "Select", label: "Select" }],
          vehicleMainTypes: [{ value: "Select", label: "Select" }],
          batteryStatuses: [{ value: "Select", label: "Select" }],
          vehicleStatuses: [{ value: "Select", label: "Select" }],
        });
      } finally {
        setDropdownLoading(false);
      }
    },
    [commonHeaders, session, filters.selectedBranchId, defaultFilters.selectedBranchId]
  );

  /* ===========================
     list fetch
  =========================== */
  const fetchList = useCallback(
    async (nextFilters = filters) => {
      try {
        setListLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            branchType: session.branchType,
            selectedBranchId: nextFilters.selectedBranchId,
            loginUserId: session.loginUserId,

            fromDate: nextFilters.fromDate,
            toDate: nextFilters.toDate,

            batteryCode: nextFilters.batteryCode,
            batteryType: nextFilters.batteryType,
            batterySerialNo: nextFilters.batterySerialNo,

            store: nextFilters.store,

            vehicleCode: nextFilters.vehicleCode,
            vehicleName: nextFilters.vehicleName,
            vehicleRegNo: nextFilters.vehicleRegNo,

            batteryStatus: nextFilters.batteryStatus,
            vehicleMainTypeIndex: nextFilters.vehicleMainTypeIndex,
            vehicleStatus: nextFilters.vehicleStatus,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load report");
        }

        setRows(toList(data.rows));
        setPage(1);
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        setRows([]);
        setPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, session, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);

      setFilters(defaultFilters);
      setRows([]);
      setPage(1);

      await loadMeta(defaultFilters.selectedBranchId);
    } finally {
      setPageLoading(false);
    }
  }, [defaultFilters, loadMeta]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback(
    async (e) => {
      const { name, value } = e.target;

      if (name === "selectedBranchId") {
        const next = {
          ...filters,
          selectedBranchId: value,
          batteryCode: "Select",
          batteryType: "Select",
          batterySerialNo: "Select",
        };

        setFilters(next);
        await loadMeta(value);
        return;
      }

      setField(name, value);
    },
    [filters, loadMeta, setField]
  );

  const handleSearch = useCallback(async () => {
    await fetchList(filters);
    toast.success("Report loaded");
  }, [fetchList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    await loadMeta(reset.selectedBranchId);
    await fetchList(reset);
    toast.info("Showing all records");
  }, [session.branchType, session.branchId, loadMeta, fetchList]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    await fetchList(defaultFilters);
    toast.info("Page refreshed");
  }, [loadPage, fetchList, defaultFilters]);

  const handleReset = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    setRows([]);
    setPage(1);
    await loadMeta(reset.selectedBranchId);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId, loadMeta]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handlePrintRow = useCallback(
    (row) => {
      const batteryId =
        row?.fld_batteryid || row?.batteryId || row?.fld_batterycardmainid;

      navigate("/vehicle/BatetryCardMaintenanceReport_Print", {
        state: { batteryId },
      });
    },
    [navigate]
  );

  const handleAllPrint = useCallback(() => {
    navigate("/vehicle/BatteryCardMaintenanceReport_AllPrint", {
      state: { filters },
    });
    toast.info("Printing all battery card records");
  }, [navigate, filters]);

  const handleExport = useCallback(() => {
    try {
      const headers = [
        "SNo",
        "BranchName",
        "VehicleMainType",
        "VehicleName",
        "VehicleRegNo",
        "BatteryCode",
        "BatteryType",
        "BatterySerialNo",
        "FittedDate",
        "CurrentDate",
        "ActualLife",
        "CurrentLife",
        "PerDayCost",
        "Alert",
      ];

      const lines = [headers.join(",")];

      rows.forEach((r, idx) => {
        const values = [
          idx + 1,
          toStr(r.fld_BranchName ?? r.branchname ?? r.branch ?? ""),
          toStr(r.VehicleType ?? r.vehicleMainType ?? ""),
          toStr(r.fld_Vehiclename ?? r.fld_vehicle_name ?? r.vehicleName ?? ""),
          toStr(r.fld_vehicle_regno ?? r.vehicleRegNo ?? ""),
          toStr(r.fld_batterycode ?? r.batteryCode ?? ""),
          toStr(r.fld_batterytype ?? r.batteryType ?? ""),
          toStr(r.fld_battery_serialno ?? r.batterySerialNo ?? ""),
          toStr(r.fld_Fitteddate ?? r.fittedDate ?? ""),
          toStr(r.currentdate ?? r.removeDate ?? ""),
          toStr(r.fld_actuallife ?? ""),
          toStr(r.fld_currentlife ?? ""),
          toStr(r.fld_perdaycost ?? ""),
          toStr(r.alert ?? ""),
        ].map((x) => `"${String(x).replace(/"/g, '""')}"`);

        lines.push(values.join(","));
      });

      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "BatteryCardMaintenanceReport.csv";
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Exported CSV");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    }
  }, [rows]);

  const goPage = useCallback(
    (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      setPage(next);
    },
    [totalPages]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    (async () => {
      await loadPage();
      await fetchList(defaultFilters);
    })();
  }, [loadPage, fetchList, defaultFilters]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATTERY CARD MAINTENANCE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Battery Card Maintenance Report
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  Total Records: {totalRows}
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isBusy
                      ? isDark
                        ? "bg-yellow-900 text-yellow-100"
                        : "bg-yellow-100 text-yellow-800"
                      : isDark
                      ? "bg-green-900 text-green-100"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {pageLoading
                    ? "Loading Page"
                    : dropdownLoading
                    ? "Loading Dropdowns"
                    : listLoading
                    ? "Loading List"
                    : "Ready"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || dropdownLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-800"
                }`}
              >
                Loading...
              </h3>
              <p
                className={`mb-6 max-w-sm mx-auto ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter />
                    FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>From Date</label>
                      <input
                        type="date"
                        name="fromDate"
                        value={filters.fromDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>To Date</label>
                      <input
                        type="date"
                        name="toDate"
                        value={filters.toDate}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        name="selectedBranchId"
                        value={filters.selectedBranchId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy || session.branchType !== "HO"}
                      >
                        {meta.branches.map((b, i) => (
                          <option key={`${b.value}-${i}`} value={b.value}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input
                        type="text"
                        name="vehicleCode"
                        value={filters.vehicleCode}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle code"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Reg No</label>
                      <input
                        type="text"
                        name="vehicleRegNo"
                        value={filters.vehicleRegNo}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter registration number"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Name</label>
                      <input
                        type="text"
                        name="vehicleName"
                        value={filters.vehicleName}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle name"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Battery Code</label>
                      <select
                        name="batteryCode"
                        value={filters.batteryCode}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {meta.batteryCodes.map((x, i) => (
                          <option key={`${x.value}-${i}`} value={x.label || x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Battery Type</label>
                      <select
                        name="batteryType"
                        value={filters.batteryType}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {meta.batteryTypes.map((x, i) => (
                          <option key={`${x.value}-${i}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Battery Serial No</label>
                      <select
                        name="batterySerialNo"
                        value={filters.batterySerialNo}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {meta.serialNos.map((x, i) => (
                          <option key={`${x.value}-${i}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Battery Status</label>
                      <select
                        name="batteryStatus"
                        value={filters.batteryStatus}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {meta.batteryStatuses.map((x, i) => (
                          <option key={`${x.value}-${i}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Main Type</label>
                      <select
                        name="vehicleMainTypeIndex"
                        value={filters.vehicleMainTypeIndex}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {meta.vehicleMainTypes.map((x, i) => (
                          <option key={`${x.value}-${i}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Status</label>
                      <select
                        name="vehicleStatus"
                        value={filters.vehicleStatus}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {meta.vehicleStatuses.map((x, i) => (
                          <option key={`${x.value}-${i}`} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading
                        ? "Loading page..."
                        : dropdownLoading
                        ? "Loading dropdowns..."
                        : listLoading
                        ? "Loading list..."
                        : "Ready"}
                    </div>

                    <div className="flex gap-4 flex-wrap">
                      <button
                        onClick={handleReset}
                        type="button"
                        disabled={isBusy}
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        Clear / Reset
                      </button>

                      <button
                        onClick={handleViewAll}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        View All
                      </button>

                      <button
                        onClick={handleSearch}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaSearch />
                        Search
                      </button>

                      <button
                        onClick={handleAllPrint}
                        type="button"
                        disabled={isBusy || rows.length === 0}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaPrint />
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {currentItems.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      No Battery Records Found
                    </h3>
                    <p
                      className={`mb-6 max-w-sm mx-auto ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Try adjusting your search criteria or click "View All".
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          <th className={thClass}>S.No</th>
                          <th className={thClass}>Branch Name</th>
                          <th className={thClass}>Vehicle Main Type</th>
                          <th className={thClass}>Vehicle Name</th>
                          <th className={thClass}>Vehicle Reg No</th>
                          <th className={thClass}>Battery Code</th>
                          <th className={thClass}>Battery Type</th>
                          <th className={thClass}>Battery Serial No</th>
                          <th className={thClass}>Fitted Date</th>
                          <th className={thClass}>Current Date</th>
                          <th className={thClass}>Actual Life</th>
                          <th className={thClass}>Current Life</th>
                          <th className={thClass}>Per Day Cost</th>
                          <th className={thClass}>Alert</th>
                          <th className={thClass}>Actions</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {currentItems.map((r, idx) => {
                          const sno = indexOfFirstItem + idx + 1;

                          const branchName =
                            r.fld_BranchName ?? r.branchname ?? r.branch ?? "";

                          const vehicleMainType =
                            r.VehicleType ?? r.vehicleMainType ?? r.fld_vehiclemaintypename ?? "";

                          const vehicleName =
                            r.fld_Vehiclename ?? r.fld_vehicle_name ?? r.vehicleName ?? "";

                          const regNo = r.fld_vehicle_regno ?? r.vehicleRegNo ?? "";

                          const batteryCode = r.fld_batterycode ?? r.batteryCode ?? "";
                          const batteryType = r.fld_batterytype ?? r.batteryType ?? "";
                          const serialNo = r.fld_battery_serialno ?? r.batterySerialNo ?? "";

                          const fittedDate = fmtDate(r.fld_Fitteddate ?? r.fittedDate ?? "");
                          const currentDate = fmtDate(r.currentdate ?? r.removeDate ?? "");

                          const actualLife = r.fld_actuallife ?? r.actualLife ?? "";
                          const currentLife = r.fld_currentlife ?? r.currentLife ?? "";
                          const perDayCost = r.fld_perdaycost ?? r.perDayCost ?? 0;
                          const alert = r.alert ?? "";

                          const alertClass =
                            alert === "Need To Change"
                              ? "text-red-600"
                              : alert === "Check Soon"
                              ? "text-yellow-600"
                              : "text-green-600";

                          return (
                            <tr
                              key={`${r.fld_batteryid ?? r.id ?? "row"}-${sno}`}
                              className={`${
                                isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                              } transition-colors duration-200`}
                            >
                              <td className={tdClass}>{sno}</td>
                              <td className={tdClass}>{branchName}</td>
                              <td className={tdClass}>{vehicleMainType}</td>
                              <td className={tdClass}>{vehicleName}</td>
                              <td className={tdClass}>{regNo}</td>
                              <td className={tdClass}>{batteryCode}</td>
                              <td className={tdClass}>{batteryType}</td>
                              <td className={tdClass}>{serialNo}</td>
                              <td className={tdClass}>{fittedDate}</td>
                              <td className={tdClass}>{currentDate}</td>
                              <td className={tdClass}>{actualLife}</td>
                              <td className={tdClass}>{currentLife}</td>
                              <td className={tdClass}>₹{fmtMoney(perDayCost)}</td>
                              <td className={`px-4 py-4 text-sm text-center font-semibold ${alertClass}`}>
                                {alert}
                              </td>
                              <td className="px-4 py-4 text-center">
                                <button
                                  onClick={() => handlePrintRow(r)}
                                  disabled={isBusy}
                                  className={`p-2 rounded ${
                                    isDark
                                      ? "bg-gray-700 text-white hover:bg-gray-600"
                                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                  } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                                  title="Print"
                                  type="button"
                                >
                                  <FaPrint className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalRows > 0 && (
                <div className="mt-6 flex justify-center items-center gap-3">
                  <button
                    className={`px-4 py-2 border rounded ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${isBusy || page <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(page - 1)}
                    disabled={isBusy || page <= 1}
                    type="button"
                  >
                    Prev
                  </button>

                  <div
                    className={`px-4 py-2 border rounded ${
                      isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
                    }`}
                  >
                    Page {page} / {totalPages}
                  </div>

                  <button
                    className={`px-4 py-2 border rounded ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${isBusy || page >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(page + 1)}
                    disabled={isBusy || page >= totalPages}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}